import type {EntityFieldItemApplier, EntityOperation} from '@github-ui/optimistic-connection'

export type OptimisticProjectItemFieldValue = {
  id: string
  field: {id: string}
} & Record<string, unknown>

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object'
}

export function upsertProjectItemFieldValue(
  itemId: string,
  fieldId: string,
  value: OptimisticProjectItemFieldValue,
): EntityOperation {
  return {
    type: 'upsertEntityFieldItem',
    id: itemId,
    field: 'fieldValues',
    key: fieldId,
    item: value,
  }
}

export function removeProjectItemFieldValue(itemId: string, fieldId: string): EntityOperation {
  return {
    type: 'removeEntityFieldItem',
    id: itemId,
    field: 'fieldValues',
    key: fieldId,
    item: {field: {id: fieldId}},
  }
}

export const applyProjectItemFieldValue: EntityFieldItemApplier = (entity, operation) => {
  if (operation.field !== 'fieldValues') return

  const fieldValues = entity.fieldValues
  if (!fieldValues || typeof fieldValues !== 'object') return

  const edges = (fieldValues as {edges?: unknown}).edges
  if (!Array.isArray(edges)) return

  const edgeIndex = edges.findIndex(edge => {
    if (!edge || typeof edge !== 'object') return false
    const node = (edge as {node?: unknown}).node
    if (!node || typeof node !== 'object') return false
    const field = (node as {field?: unknown}).field
    return field != null && typeof field === 'object' && (field as {id?: unknown}).id === operation.key
  })

  if (operation.type === 'removeEntityFieldItem') {
    if (edgeIndex !== -1) edges.splice(edgeIndex, 1)
    return
  }

  const nextEdge = {node: operation.item}
  if (edgeIndex === -1) edges.push(nextEdge)
  else edges[edgeIndex] = nextEdge
}

export function projectItemFieldValueFromResponse(
  fieldValues: {readonly edges?: ReadonlyArray<{readonly node?: unknown} | null | undefined> | null} | null | undefined,
  fieldId: string,
): OptimisticProjectItemFieldValue | undefined {
  const nodes = fieldValues?.edges?.flatMap<Record<string, unknown>>(edge => (isRecord(edge?.node) ? [edge.node] : []))
  const node = nodes?.find(value => {
    const field = value.field
    return isRecord(field) && field.id === fieldId
  })

  if (!node || typeof node.id !== 'string') return undefined
  return {...node, id: node.id, field: {id: fieldId}}
}
